/**
 * Currency registry for JP MARINE. Document-level currency lives on
 * Invoice/Quote/Contract/Payment via the `currency` column. The marina's
 * base currency for reporting is IDR (settings.currency); foreign
 * documents store an FX snapshot in `fxRateToBase` so historical reports
 * stay accurate even as live rates move.
 */
export type CurrencyCode = 'IDR' | 'USD' | 'EUR' | 'AUD' | 'SGD' | 'JPY' | 'GBP'

export interface CurrencyMeta {
  code: CurrencyCode
  symbol: string
  /** Decimal places to render. JPY has 0, USD/EUR/etc 2. IDR rendered at 0 (sub-rupiah dropped). */
  decimals: number
  /** Where the symbol sits relative to the number. */
  symbolPosition: 'prefix' | 'suffix'
  /** Locale for thousand separator. en-US gives commas. */
  locale: string
}

export const CURRENCIES: Record<CurrencyCode, CurrencyMeta> = {
  IDR: { code: 'IDR', symbol: 'Rp', decimals: 0, symbolPosition: 'prefix', locale: 'en-US' },
  USD: { code: 'USD', symbol: '$', decimals: 2, symbolPosition: 'prefix', locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', decimals: 2, symbolPosition: 'prefix', locale: 'en-US' },
  AUD: { code: 'AUD', symbol: 'A$', decimals: 2, symbolPosition: 'prefix', locale: 'en-US' },
  SGD: { code: 'SGD', symbol: 'S$', decimals: 2, symbolPosition: 'prefix', locale: 'en-US' },
  JPY: { code: 'JPY', symbol: '¥', decimals: 0, symbolPosition: 'prefix', locale: 'en-US' },
  GBP: { code: 'GBP', symbol: '£', decimals: 2, symbolPosition: 'prefix', locale: 'en-US' },
}

export const CURRENCY_OPTIONS = Object.values(CURRENCIES).map((c) => ({
  value: c.code,
  label: `${c.code} · ${c.symbol}`,
}))

export function isValidCurrency(value: string | null | undefined): value is CurrencyCode {
  return value !== null && value !== undefined && value in CURRENCIES
}

export function getCurrency(code: string | null | undefined): CurrencyMeta {
  return isValidCurrency(code) ? CURRENCIES[code] : CURRENCIES.IDR
}

/**
 * Format an amount in its document currency. Falls back to IDR when no
 * code is supplied so legacy callers continue to render unchanged.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined = 'IDR',
  fallback = '-',
): string {
  if (amount === null || amount === undefined) return fallback
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(n)) return fallback
  const meta = getCurrency(currency)
  const formatter = new Intl.NumberFormat(meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  })
  const formatted = formatter.format(n)
  return meta.symbolPosition === 'prefix' ? `${meta.symbol} ${formatted}` : `${formatted} ${meta.symbol}`
}
